/**
 *
 * Resource
 *
 */

import { BehaviorSubject } from 'rxjs';

export type ResourceFetcher<D, P> = (params: P) => Promise<D>;

export type ResourceFetch<P> = (params?: P) => void;
export type ResourceRefetch<P> = (params?: P) => void;
export type ResourceRetry = () => void;

export interface ResourceState<D, P> {
  readonly params: P;
  readonly data: D | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
}

export interface Resource<D, P> {
  readonly $: BehaviorSubject<ResourceState<D, P>>;
  readonly state: ResourceState<D, P>;
  /**
   * Calls the `fetcher` and updates the resource value when resolved.
   * If an `error` occurs the `data` wont be updated.
   * If no `params` are passed, the last ones are used.
   */
  fetch: ResourceFetch<P>;
  /**
   * Destroyes the data then calls the `fetcher` and updates the resource value when resolved.
   * If an `error` occurs the `data` will stay `undefined`.
   * If no `params` are passed, the last ones are used.
   */
  refetch: ResourceRefetch<P>;
  /**
   * Just a wrapper around `refetch` which takes no arguments.
   * Useful for passing to the component event handlers.
   */
  retry: ResourceRetry;
  /**
   * Updates the resource `data` in place for local mutations.
   */
  updateData: (updater: (data: Readonly<D | undefined>) => D | undefined) => void;
  /**
   * Updates the resource `params` and calls the `fetcher`.
   */
  updateParams: (updater: (params: Readonly<P>) => P) => void;
}

export function createResource<Data, Params>(
  fetcher: ResourceFetcher<Data, Params>,
  initialParams: Params,
): Resource<Data, Params> {
  const source$ = new BehaviorSubject<ResourceState<Data, Params>>({
    params: initialParams,
    data: undefined,
    loading: false,
    error: null,
  });

  const fetch: ResourceFetch<Params> = async (params) => {
    params = params || source$.value.params;
    source$.next({
      ...source$.value,
      params,
      loading: true,
      error: null,
    });
    try {
      const data = await fetcher(params);
      source$.next({ ...source$.value, data, loading: false, error: null });
    } catch (error) {
      source$.next({ ...source$.value, loading: false, error });
    }
  };

  const refetch: ResourceRefetch<Params> = async (params) => {
    source$.next({ ...source$.value, data: undefined });
    await fetch(params);
  };

  const retry: ResourceRetry = async () => await refetch();

  return {
    $: source$,
    get state() {
      return source$.value;
    },
    fetch,
    refetch,
    retry,
    updateData: (updater) => source$.next({ ...source$.value, data: updater(source$.value.data) }),
    updateParams: (updater) => fetch(updater(source$.value.params)),
  };
}
